import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { STATUS_COLORS } from '../../lib/format';
import { Search, Loader2 } from 'lucide-react';

/** Async searchable lead picker — links a meeting to exactly one existing lead. */
/** Company + phone subtitle, computed outside JSX. */
const leadMeta = (l) => [l.company_name, l.phone].filter(Boolean).join(' · ');

/** Debounced lead search (min 2 chars, top 8). */
const useLeadSearch = (term) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (term.trim().length < 2) { setResults([]); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/leads', { params: { search: term.trim(), page: 1, page_size: 8 } })
        .then(({ data }) => setResults(data.items || data || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [term]);
  return { results, loading };
};

/** Close-on-outside-click for the results dropdown. */
const useOutsideClose = (boxRef, setOpen) => {
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [boxRef, setOpen]);
};

const SelectedLeadCard = ({ value, onSelect }) => (
  <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2" data-testid="meeting-linked-lead">
    <div className="min-w-0">
      <div className="text-sm font-medium truncate">{value.lead_name}</div>
      <div className="text-[11px] text-muted-foreground truncate">{leadMeta(value) || 'No company/phone'}</div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[value.status] || ''}`}>{value.status}</Badge>
      <button type="button" className="text-xs text-primary hover:underline" onClick={() => onSelect(null)} data-testid="meeting-change-lead-btn">
        Change
      </button>
    </div>
  </div>
);

const LeadResultsList = ({ results, loading, onPick }) => (
  <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-lg max-h-56 overflow-y-auto" data-testid="meeting-lead-results">
    {results.map((l) => (
      <button key={l.id} type="button" onClick={() => onPick(l)}
        className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between gap-2"
        data-testid={`meeting-lead-option-${l.id}`}>
        <span className="min-w-0">
          <span className="block text-sm font-medium truncate">{l.lead_name}</span>
          <span className="block text-[11px] text-muted-foreground truncate">{leadMeta(l)}</span>
        </span>
        <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[l.status] || ''}`}>{l.status}</Badge>
      </button>
    ))}
    {!loading && results.length === 0 && (
      <div className="px-3 py-3 text-xs text-muted-foreground">No matching leads</div>
    )}
  </div>
);

export const LeadSearchSelect = ({ value, onSelect }) => {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { results, loading } = useLeadSearch(term);
  useOutsideClose(boxRef, setOpen);

  if (value) return <SelectedLeadCard value={value} onSelect={onSelect} />;

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={term} placeholder="Search lead by name, company or phone…" className="pl-8"
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} data-testid="meeting-lead-search-input" />
        {loading && <Loader2 size={14} className="absolute right-2.5 top-2.5 animate-spin text-muted-foreground" />}
      </div>
      {open && term.trim().length >= 2 && (
        <LeadResultsList results={results} loading={loading}
          onPick={(l) => { onSelect(l); setOpen(false); setTerm(''); }} />
      )}
    </div>
  );
};
